import i18n from 'helpers/i18n';
import messages from './messages';
import { useCallback, useState } from 'react';
import { DimensionCard } from './DimensionCard/DimensionCard';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { ObservationCycleScore } from 'types/api/envScales/ObservationCycleScore';
import { SectionCollapse } from 'components/SectionCollapse';
import {
  useValidations,
  getSubsectionStatus
} from 'components/Measure/View/ValidationsContext';
import {
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@teachstonellc/component-library';
import { InfoCircle } from '@teachstonellc/component-library/dist/icons';

interface CycleScoresFormProp {
  /** Age Level. */
  ageLevel: AgeLevel;

  /** Observation Cycle. */
  cycle: ObservationCycle;

  /** Array of Observation Cycle Scores. */
  cycleScores: ObservationCycleScore[];

  /** Indicates if the observation is informal. */
  isInformal: boolean;

  /** Indicates if notes are enabled. */
  isNotesEnabled: boolean;

  /** Notes by dimension ID. */
  notesByDimensionId: Record<number, string>;
}

function getInitialScores(
  dimensionId: number,
  cycleScores: ObservationCycleScore[]
) {
  const dimensionScores = cycleScores.find(
    score => score.dimension_id === dimensionId
  );

  if (dimensionScores) {
    return dimensionScores;
  } else {
    return { dimension_score: null, indicator_scores: [] };
  }
}

function CycleScoresForm({
  cycle,
  cycleScores,
  ageLevel,
  isInformal,
  isNotesEnabled,
  notesByDimensionId
}: CycleScoresFormProp) {
  const [activeDimension, setActiveDimension] = useState<number>();
  const { sections, errors, resolvedPaths, showErrorStatus } = useValidations();

  const handleExpandCollapse = useCallback(
    (dimensionId: number, isExpanded: boolean) => {
      if (isExpanded) {
        setActiveDimension(dimensionId);
      } else {
        setActiveDimension(undefined);
      }
    },
    []
  );

  function getSectionStatus() {
    if (isInformal) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-auto">
              <Label color="yellow" endIcon={<InfoCircle />}>
                {i18n.ft(messages.optional)}
              </Label>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-sm text-center">
            <div>{i18n.ft(messages.optionalTooltipLine1)}</div>
            <div>{i18n.ft(messages.optionalTooltipLine2)}</div>
          </TooltipContent>
        </Tooltip>
      );
    }

    if (showErrorStatus && sections) {
      return getSubsectionStatus(
        sections.cycles[cycle.cycle_number],
        'scores',
        showErrorStatus,
        { errors, resolvedPaths, prefix: `cycles.${cycle.cycle_number}.scores` }
      );
    }

    return null;
  }

  const labelTypes = {
    formal: {
      title: i18n.ft(messages.enterScores),
      description: i18n.ft(messages.sectionDescription)
    },
    informal: {
      title: i18n.ft(messages.enterRanges),
      description: i18n.ft(messages.enterRangesDescription)
    }
  };

  const labels = isInformal ? labelTypes.informal : labelTypes.formal;

  return (
    <SectionCollapse
      className="mt-2"
      title={labels.title}
      subtitle={labels.description}
      status={getSectionStatus()}
      closeOthersOnOpen
    >
      {ageLevel.domains.map(domain => {
        return domain.dimensions.map(dimension => {
          const initialScores = getInitialScores(dimension.id, cycleScores);

          return (
            <DimensionCard
              key={dimension.id}
              cycle={cycle}
              domain={domain}
              dimension={dimension}
              initialScore={initialScores.dimension_score}
              initialIndicators={initialScores.indicator_scores}
              isExpanded={activeDimension === dimension.id}
              onExpandCollapse={handleExpandCollapse}
              isInformal={isInformal}
              isNotesEnabled={isNotesEnabled}
              observationNotes={notesByDimensionId[dimension.id] ?? ''}
            />
          );
        });
      })}
    </SectionCollapse>
  );
}

export default CycleScoresForm;
